import { Grid, Typography } from '@material-ui/core'
import React, { useContext, useEffect, useMemo } from 'react'

import useLegalAgreements, { GuardedScope } from '../../../hooks/useLegalAgreements'
import CheckoutLayout from '../../../layouts/buy/CheckoutLayout'
import { CheckoutNavigationBarOptionsContext } from '../../../layouts/context'
import { useAuth } from '../../../lib/auth'
import useCheckoutNavBarOptionValue from '../../../lib/cart/hooks/useCheckoutNavBarOptionValue'
import { usePageUrl } from '../../../lib/pages'
import { displayNameForAgreementId } from '../../../utils/legalAgreements'
import ReviewPendingAgreement from '../../global/agreements/ReviewPendingAgreement'
import ClickWrapContextProvider from '../../global/clickwrap/ClickWrapContextProvider'
import ClickWrapModal from '../../global/clickwrap/ClickWrapModal'
import { ClickWrapModalContext } from '../../global/clickwrap/context'
import Spinner from '../../Spinner'
import { GridButton } from '../shared/GridButton'
import useStyles from './styles/agreements'

const Agreements = () => {
  const classes = useStyles()
  const { user } = useAuth()
  const { accepted, pending, allowedScopes, requiredForScope } = useLegalAgreements()
  const [, modalDispatch] = useContext(ClickWrapModalContext)
  const { navBarOptionValue } = useCheckoutNavBarOptionValue('agreements')
  const { displayAgreements, loading: loadingNavBarOptions } = useContext(
    CheckoutNavigationBarOptionsContext,
  )
  const { navigate } = usePageUrl()
  const openClickWrap = () => {
    modalDispatch({ type: 'open' })
  }
  const loadingUser = !user
  const hasAccepted = Object.keys(accepted).length

  const requiredForCheckout = useMemo(
    () => requiredForScope(GuardedScope.CHECKOUT),
    [requiredForScope],
  )

  const isRequired = (agreementId) => requiredForCheckout.indexOf(agreementId) >= 0

  const isCheckoutAllowed = useMemo(
    () => allowedScopes.indexOf(GuardedScope.CHECKOUT) >= 0,
    [allowedScopes],
  )
  const isContinueDisabled = !isCheckoutAllowed

  useEffect(() => {
    if (!displayAgreements) {
      return navigate('/checkout/shipping')
    }
  }, [displayAgreements, loadingNavBarOptions, loadingUser, navigate])

  if (loadingUser || loadingNavBarOptions) return <Spinner loading />

  return (
    <>
      {displayAgreements && (
        <CheckoutLayout checkoutNavigationBarTabValue={navBarOptionValue}>
          <Grid alignItems="center" justify="center" className="" container item xs={12}>
            <Grid item xs={12} md={8}>
              <Spinner loading={loadingUser} />
              <div className={classes.descriptionSection}>
                <Typography variant="h4" align="center" className={classes.descriptionText}>
                  We’ve updated our Agreements
                </Typography>
                <Typography variant="body2" className={classes.descriptionText}>
                  Please note that we have updated the below agreement(s) that relate to your DFH
                  account. Please take a moment to read each one carefully and agree to the terms
                  stated before proceeding to checkout.
                </Typography>
                <Typography variant="body2" className={classes.descriptionText}>
                  For any questions relating to the new agreements contact your DFH representative
                  or call our Customer Experience team on 860-623-6314.
                </Typography>
                <Typography variant="body2" className={classes.descriptionText}>
                  We thank you for your cooperation and support.
                </Typography>
              </div>
              {!loadingUser && (
                <>
                  {pending && pending.length > 0 && (
                    <>
                      <div className={classes.agreementBox}>
                        <Typography variant="h5">
                          PENDING AGREEMENTS{' '}
                          {requiredForCheckout.length > 0 && (
                            <span className={classes.agreementRequired}>*Required</span>
                          )}
                        </Typography>
                        <div className={classes.agreementBoxSectionContent}>
                          <div>
                            <ul className="agreement-list">
                              {pending.map((agreementId) => (
                                <li className="pending-agreement-item" key={agreementId}>
                                  {displayNameForAgreementId(agreementId)}{' '}
                                  <strong>{isRequired(agreementId) ? '*' : null}</strong>
                                </li>
                              ))}
                            </ul>
                            <ClickWrapModal>
                              <ClickWrapContextProvider accepted={false}>
                                {pending.length > 0 && (
                                  <ReviewPendingAgreement agreementId={pending[0]} />
                                )}
                              </ClickWrapContextProvider>
                            </ClickWrapModal>
                          </div>
                        </div>
                      </div>
                      <Grid justify="center" container item xs={12}>
                        <GridButton
                          onClick={openClickWrap}
                          id="continue-to-payment-button"
                          disabled={!pending.length}
                        >
                          REVIEW AGREEMENTS
                        </GridButton>
                      </Grid>
                    </>
                  )}
                  <div className={classes.agreementBox}>
                    <Typography variant="h5">ACCEPTED AGREEMENTS</Typography>
                    <div className={classes.agreementBoxSectionContent}>
                      {hasAccepted > 0 && (
                        <ul className="agreement-list">
                          {Object.keys(accepted).map((acceptedDocument, index) => (
                            <li key={index}>
                              {displayNameForAgreementId(acceptedDocument)}{' '}
                              <strong>{isRequired(acceptedDocument) ? '*' : null}</strong>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                  <Grid justify="center" container item xs={12}>
                    <GridButton
                      onClick={() => navigate('/checkout/shipping')}
                      id="continue-to-payment-button"
                      disabled={isContinueDisabled}
                    >
                      CONTINUE TO SHIPPING
                    </GridButton>
                  </Grid>
                </>
              )}
            </Grid>
          </Grid>
        </CheckoutLayout>
      )}
    </>
  )
}

export default Agreements
